import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MessageCircle, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { careTokenHeaders } from "@/lib/care-token";
import {
  canRenderTargetedMessages,
  clearTargetedConversationCache,
  clearTargetedConversationListCache,
  isTargetedAccessRevocationError,
  getTargetedAccessRevocationStatus,
  reconcileTargetedConversationSelection,
  targetedConversationsQueryKey,
  targetedMessagesQueryKey,
} from "@/lib/targeted-messenger-revocation";

type TargetMember = {
  id: string;
  username: string;
  displayName: string;
  avatarUrl?: string;
  isOwner?: boolean;
};

type TargetedConversation = {
  id: string;
  targetMemberUserId: string;
  name: string;
  unreadCount: number;
};

type TargetedMessage = {
  id: string;
  senderType: "caregiver" | "care_recipient";
  senderName?: string;
  content: string;
  timestamp: string;
  isRead: boolean;
};

async function fetchCareJson<T>(hubId: string, url: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  for (const [name, value] of Object.entries(careTokenHeaders(hubId))) {
    headers.set(name, value);
  }
  headers.set("x-care-view-request", "true");
  const response = await fetch(url, {
    ...init,
    headers,
    credentials: "include",
  });
  if (!response.ok) {
    let message = response.statusText;
    try {
      message = (await response.json()).message || message;
    } catch {
      // Keep the HTTP status text for non-JSON responses.
    }
    throw new Error(`${response.status}: ${message}`);
  }
  return await response.json() as T;
}

export default function TargetedCareMessengerPanel({
  hubId,
  onSelectedConversationChange,
}: {
  hubId: string;
  onSelectedConversationChange?: (conversationId: string | null) => void;
}) {
  const queryClient = useQueryClient();
  const messagePaneRef = useRef<HTMLDivElement>(null);
  const messageScrollStateRef = useRef<{
    conversationKey: string | null;
    initialPositioned: boolean;
    knownMessageIds: Set<string>;
    nearBottom: boolean;
  }>({
    conversationKey: null,
    initialPositioned: false,
    knownMessageIds: new Set<string>(),
    nearBottom: true,
  });
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [messageInput, setMessageInput] = useState("");
  const conversationScrollKey = selectedConversationId
    ? `${hubId}:${selectedConversationId}`
    : null;
  const targetMembersKey = [`/api/messenger/${hubId}/target-members`];
  const conversationsKey = targetedConversationsQueryKey(hubId);
  const messagesKey = targetedMessagesQueryKey(hubId, selectedConversationId);

  const {
    data: membersData,
    isLoading: membersLoading,
    isError: membersError,
    error: membersQueryError,
  } = useQuery<TargetMember[]>({
    queryKey: targetMembersKey,
    queryFn: () => fetchCareJson(hubId, `/api/messenger/${hubId}/target-members`),
    refetchInterval: 30000,
    staleTime: 10000,
  });
  const members = membersData ?? [];

  const {
    data: conversationsData,
    isLoading: conversationsLoading,
    isError: conversationsError,
    error: conversationsQueryError,
  } = useQuery<TargetedConversation[]>({
    queryKey: conversationsKey,
    queryFn: () => fetchCareJson(hubId, `/api/messenger/${hubId}/targeted-conversations`),
    refetchInterval: 5000,
    staleTime: 5000,
  });
  const conversations = conversationsData ?? [];

  const {
    data: messagesData,
    isLoading: messagesLoading,
    error: messagesQueryError,
  } = useQuery<TargetedMessage[]>({
    queryKey: messagesKey,
    queryFn: () => fetchCareJson(
      hubId,
      `/api/messenger/${hubId}/targeted-conversations/${selectedConversationId}/messages`,
    ),
    enabled: Boolean(selectedConversationId),
    refetchInterval: 5000,
    staleTime: 3000,
  });
  const messages = messagesData ?? [];

  const clearSelection = useCallback((clearConversationList = false) => {
    clearTargetedConversationCache(queryClient, hubId, selectedConversationId);
    if (clearConversationList) {
      clearTargetedConversationListCache(queryClient, hubId);
      queryClient.setQueryData<TargetMember[]>(targetMembersKey, []);
    }
    setSelectedConversationId(null);
  }, [hubId, queryClient, selectedConversationId, targetMembersKey]);

  const selectedConversation = conversations.find((item) => item.id === selectedConversationId);
  const selectedConversationIsCurrent = Boolean(
    selectedConversation
    && members.some((member) => member.id === selectedConversation.targetMemberUserId),
  );
  const canUseSelectedConversation = canRenderTargetedMessages({
    selectedConversationId,
    selectedConversationIsCurrent,
    conversationListError: conversationsQueryError,
    memberListError: membersQueryError,
    messagesError: messagesQueryError,
  });
  const visibleMessages = canUseSelectedConversation ? messages : [];

  useEffect(() => {
    const nextSelection = reconcileTargetedConversationSelection(queryClient, hubId, {
      selectedConversationId,
      conversations: conversationsData,
      conversationListLoaded: conversationsData !== undefined,
      conversationListError: conversationsQueryError,
      messagesError: messagesQueryError,
      eligibleMemberIds: membersData
        ? new Set(membersData.map((member) => member.id))
        : undefined,
      memberListLoaded: membersData !== undefined,
      memberListError: membersQueryError,
    });
    if (
      isTargetedAccessRevocationError(conversationsQueryError)
      || isTargetedAccessRevocationError(membersQueryError)
    ) {
      const cachedMembers = queryClient.getQueryData<TargetMember[]>(targetMembersKey);
      if (cachedMembers?.length) {
        queryClient.setQueryData<TargetMember[]>(targetMembersKey, []);
      }
    }
    if (nextSelection !== selectedConversationId) {
      hasAutoSelectedConversation.current = true;
      setSelectedConversationId(nextSelection);
    }
  }, [
    conversationsData,
    conversationsQueryError,
    hubId,
    membersData,
    membersQueryError,
    messagesQueryError,
    queryClient,
    selectedConversationId,
    targetMembersKey,
  ]);

  useEffect(() => {
    onSelectedConversationChange?.(selectedConversationId);
  }, [onSelectedConversationChange, selectedConversationId]);

  useLayoutEffect(() => {
    const scrollState = messageScrollStateRef.current;
    if (scrollState.conversationKey !== conversationScrollKey) {
      scrollState.conversationKey = conversationScrollKey;
      scrollState.initialPositioned = false;
      scrollState.knownMessageIds = new Set();
      scrollState.nearBottom = true;
    }

    const messagePane = messagePaneRef.current;
    if (
      !conversationScrollKey
      || !messagePane
      || messagesLoading
      || !canUseSelectedConversation
    ) {
      return;
    }

    const hasNewMessage = visibleMessages.some(
      (message) => !scrollState.knownMessageIds.has(message.id),
    );

    if (!scrollState.initialPositioned) {
      scrollState.initialPositioned = true;
      messagePane.scrollTop = messagePane.scrollHeight;
      scrollState.nearBottom = true;
    } else if (hasNewMessage && scrollState.nearBottom) {
      messagePane.scrollTo({
        top: messagePane.scrollHeight,
        behavior: "smooth",
      });
      scrollState.nearBottom = true;
    }

    scrollState.knownMessageIds = new Set(visibleMessages.map((message) => message.id));
  }, [
    canUseSelectedConversation,
    conversationScrollKey,
    messagesLoading,
    visibleMessages,
  ]);

  const conversationByMember = useMemo(
    () => new Map(conversations.map((conversation) => [conversation.targetMemberUserId, conversation])),
    [conversations],
  );

  const createConversation = useMutation({
    mutationFn: (targetMemberUserId: string) => fetchCareJson<TargetedConversation>(
      hubId,
      `/api/messenger/${hubId}/targeted-conversations`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ targetMemberUserId }),
      },
    ),
    onSuccess: async (conversation) => {
      queryClient.setQueryData<TargetedConversation[]>(conversationsKey, (current) => [
        conversation,
        ...(current || []).filter((item) => item.id !== conversation.id),
      ]);
      setSelectedConversationId(conversation.id);
      await queryClient.invalidateQueries({ queryKey: conversationsKey });
    },
    onError: (error, targetMemberUserId) => {
      const status = getTargetedAccessRevocationStatus(error);
      if (status === null) return;
      if (status === 401) {
        clearSelection(true);
      } else {
        queryClient.setQueryData<TargetMember[]>(targetMembersKey, (current) =>
          current?.filter((member) => member.id !== targetMemberUserId),
        );
        clearSelection();
      }
    },
  });

  const sendMessage = useMutation({
    mutationFn: (content: string) => fetchCareJson(
      hubId,
      `/api/messenger/${hubId}/targeted-conversations/${selectedConversationId}/messages`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content }),
      },
    ),
    onSuccess: async () => {
      setMessageInput("");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: messagesKey }),
        queryClient.invalidateQueries({ queryKey: conversationsKey }),
      ]);
    },
    onError: (error) => {
      if (isTargetedAccessRevocationError(error)) clearSelection();
    },
  });

  const markRead = useMutation({
    mutationFn: () => fetchCareJson(
      hubId,
      `/api/messenger/${hubId}/targeted-conversations/${selectedConversationId}/mark-read`,
      { method: "POST" },
    ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: messagesKey }),
    onError: (error) => {
      if (isTargetedAccessRevocationError(error)) clearSelection();
    },
  });

  useEffect(() => {
    if (!canUseSelectedConversation || !visibleMessages.some((message) => !message.isRead) || markRead.isPending) return;
    const timer = window.setTimeout(() => markRead.mutate(), 400);
    return () => window.clearTimeout(timer);
  }, [canUseSelectedConversation, markRead.isPending, selectedConversationId, visibleMessages]);

  const submitMessage = (event: FormEvent) => {
    event.preventDefault();
    const content = messageInput.trim();
    if (content && canUseSelectedConversation && !sendMessage.isPending) sendMessage.mutate(content);
  };

  const loading = membersLoading || conversationsLoading;
  const error = createConversation.error || sendMessage.error || (membersError || conversationsError
    ? new Error("Targeted messages could not be loaded. Check the Care View connection and try again.")
    : null);

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white" style={{ height: "500px" }}>
      <div className="flex h-full min-h-0 flex-col">
        <div className="border-b border-gray-200 bg-gray-50 p-3">
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
            People you can message
          </p>
          {loading ? (
            <p className="text-sm text-gray-500">Loading members…</p>
          ) : membersError ? (
            <p role="alert" className="text-sm text-red-600">
              Member Messages could not be loaded. Check the Care View connection and try again.
            </p>
          ) : members.length === 0 ? (
            <p className="text-sm text-gray-500">No eligible people are available for a direct conversation.</p>
          ) : (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {members.map((member) => {
                const conversation = conversationByMember.get(member.id);
                const selected = conversation?.id === selectedConversationId;
                return (
                  <button
                    key={member.id}
                    type="button"
                    aria-pressed={selected}
                    disabled={createConversation.isPending}
                    onClick={() => {
                      if (conversation) setSelectedConversationId(conversation.id);
                      else createConversation.mutate(member.id);
                    }}
                    className={`shrink-0 rounded-full border px-3 py-1.5 text-sm transition-colors disabled:opacity-60 ${
                      selected
                        ? "border-blue-600 bg-blue-600 text-white"
                        : "border-gray-300 bg-white text-gray-700 hover:border-blue-400 hover:bg-blue-50"
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-200 text-xs font-semibold text-gray-600">
                        {member.avatarUrl
                          ? <img src={member.avatarUrl} alt="" className="h-full w-full object-cover" />
                          : member.displayName.split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase()}
                      </span>
                      <span>
                        <span className="block">{member.displayName}</span>
                        {member.isOwner && <span className="block text-[11px] opacity-75">Hub Owner</span>}
                      </span>
                    </span>
                    {conversation?.unreadCount ? ` (${conversation.unreadCount})` : ""}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex items-center justify-between border-b bg-white px-4 py-2">
            <div>
              <p className="font-medium text-gray-900">
                {canUseSelectedConversation ? selectedConversation?.name || "Member conversation" : "Select a current Hub member"}
              </p>
              <p className="text-xs text-gray-500">Private messages with one current Hub member</p>
            </div>
            <MessageCircle className="h-5 w-5 text-blue-600" aria-hidden="true" />
          </div>

          <div
            ref={messagePaneRef}
            data-testid="care-messenger-message-list"
            onScroll={(event) => {
              const pane = event.currentTarget;
              messageScrollStateRef.current.nearBottom =
                pane.scrollHeight - pane.clientHeight - pane.scrollTop <= 72;
            }}
            className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4"
          >
            {loading || messagesLoading ? (
              <p className="py-8 text-center text-sm text-gray-500">Loading targeted messages…</p>
            ) : !canUseSelectedConversation ? (
              <div className="flex h-full flex-col items-center justify-center text-center text-gray-500">
                <MessageCircle className="mb-2 h-10 w-10 text-gray-300" />
                <p>Select a current Hub member to start a private conversation.</p>
              </div>
            ) : visibleMessages.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-500">No replies yet. Send the first message.</p>
            ) : visibleMessages.map((message) => {
              const careViewMessage = message.senderType === "care_recipient";
              return (
                <div key={message.id} className={`flex ${careViewMessage ? "justify-start" : "justify-end"}`}>
                  <div className={`max-w-[82%] rounded-2xl px-4 py-2 ${
                    careViewMessage ? "bg-gray-100 text-gray-900" : "bg-blue-600 text-white"
                  }`}>
                    {!careViewMessage && message.senderName && (
                      <p className="mb-1 text-xs opacity-75">{message.senderName}</p>
                    )}
                    <p className="whitespace-pre-wrap break-words text-sm">{message.content}</p>
                    <p className="mt-1 text-right text-[11px] opacity-70">
                      {new Date(message.timestamp).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <form onSubmit={submitMessage} className="flex gap-2 border-t bg-white p-3">
            <Input
              value={messageInput}
              onChange={(event) => setMessageInput(event.target.value)}
              placeholder={canUseSelectedConversation ? "Type a private message…" : "Choose a Hub member first"}
              disabled={!canUseSelectedConversation || sendMessage.isPending}
              maxLength={4000}
            />
            <Button
              type="submit"
              aria-label="Send private message"
              disabled={!canUseSelectedConversation || !messageInput.trim() || sendMessage.isPending}
            >
              <Send className="h-4 w-4" />
            </Button>
          </form>
          {error && (
            <p role="alert" className="border-t border-red-100 bg-red-50 px-3 py-1.5 text-xs text-red-700">
              {error.message}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}